import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Star, Trophy, Users, ShieldCheck, BadgeCheck } from "lucide-react";
import { MaskedLines } from "@/components/common/Reveal";
import HeroCalendar from "./HeroCalendar";
import { HERO_SLIDES } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const BADGES = [
  { icon: Trophy, title: "#1 Largest Crew", sub: "In all of Ottawa" },
  { icon: Users, title: "20+ Detailers", sub: "Across the city" },
  { icon: ShieldCheck, title: "Fully Insured", sub: "Total peace of mind" },
  { icon: BadgeCheck, title: "Satisfaction Guarantee", sub: "Love it or we return" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.14]);

  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[92vh] flex-col overflow-hidden bg-brand-charcoal">
      {/* Background slideshow */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 h-[114%] w-full">
        <AnimatePresence>
          <motion.img
            key={slide}
            src={HERO_SLIDES[slide].src}
            alt="Reverence Autos detailing project in Ottawa"
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.4, ease: "easeInOut" }, scale: { duration: 6, ease: "linear" } }}
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: HERO_SLIDES[slide].pos }}
          />
        </AnimatePresence>
      </motion.div>

      {/* Readability scrims */}
      <div className="absolute inset-0 bg-brand-charcoal/45" />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal via-brand-charcoal/55 to-brand-charcoal/10" />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-brand-charcoal via-brand-charcoal/60 to-transparent" />

      {/* Content */}
      <div className="container-rv relative z-10 grid flex-1 items-center gap-12 pb-10 pt-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:pt-24">
        {/* Left: copy */}
        <div>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 backdrop-blur-sm"
          >
            <span className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-brand-red text-brand-red" />)}</span>
            <span className="text-xs font-medium text-white/90">Loved across Ottawa</span>
          </motion.div>

          <h1 className="font-serif text-[13vw] font-bold leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-[5.2rem]">
            <MaskedLines
              lines={["Fall in Love", <>With Your <span className="text-brand-red">Car</span> Again</>]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-7 max-w-md text-base text-white/85 sm:text-lg"
          >
            Ottawa's largest detailing crew comes to you — book in 60 seconds and drive
            away with that showroom-fresh feeling by tonight.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 flex flex-wrap items-center gap-6"
          >
            <div>
              <p className="font-serif text-3xl font-bold text-white">20+</p>
              <p className="text-xs uppercase tracking-wider text-brand-silver">Pro Detailers</p>
            </div>
            <div className="h-10 w-px bg-white/25" />
            <div>
              <p className="font-serif text-3xl font-bold text-white">60s</p>
              <p className="text-xs uppercase tracking-wider text-brand-silver">To Book</p>
            </div>
            <div className="h-10 w-px bg-white/25" />
            <div>
              <p className="font-serif text-3xl font-bold text-white">7</p>
              <p className="text-xs uppercase tracking-wider text-brand-silver">Areas Served</p>
            </div>
          </motion.div>
        </div>

        {/* Right: calendar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
        >
          <HeroCalendar />
        </motion.div>
      </div>

      {/* Trust badges + slideshow dots */}
      <div className="container-rv relative z-10 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          data-testid="hero-trust-badges"
          className="grid grid-cols-2 gap-x-6 gap-y-5 border-t border-white/15 pt-7 lg:grid-cols-4"
        >
          {BADGES.map((it) => (
            <div key={it.title} className="flex items-center gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-white/15 bg-white/10 text-brand-red backdrop-blur-sm">
                <it.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{it.title}</p>
                <p className="text-xs text-brand-silver">{it.sub}</p>
              </div>
            </div>
          ))}
        </motion.div>

        <div className="mt-7 flex justify-center gap-2">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              data-testid={`hero-slide-dot-${i}`}
              aria-label={`Show project ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${i === slide ? "w-7 bg-brand-red" : "w-1.5 bg-white/50 hover:bg-white"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
